import type { FileDownloadResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf } from "~/classes/generated/datagram"
import { FileDownloadRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const _console = useLogger("file/download")

export function parseFileDownloadRequestDatagramBuf(
  datagram: FileDownloadRequestDatagramBuf,
): FileDownloadRequestDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    file: parseFile(datagram.file()!),
    offline: datagram.offline(),
    onlineOnly: datagram.onlineOnly(),
  }
}

export function createFileDownloadRequestDatagram(requests: FileDownloadRequestDatagram[]) {
  const builder = new DatagramBufferBuilder(64)
  const downloads = []
  for (const request of requests) {
    const file = addFileToBuf(builder, request.file)
    const requestId = addRequestIdToBuf(builder, request.requestId)
    FileDownloadRequestDatagramBuf.startFileDownloadRequestDatagramBuf(builder)
    FileDownloadRequestDatagramBuf.addRequestId(builder, requestId)
    FileDownloadRequestDatagramBuf.addFile(builder, file)
    FileDownloadRequestDatagramBuf.addOffline(builder, request.offline)
    FileDownloadRequestDatagramBuf.addOnlineOnly(builder, request.onlineOnly)
    const downloadRequest = FileDownloadRequestDatagramBuf.endFileDownloadRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addFileDownloadRequest(builder, downloadRequest)
    downloads.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const requestVector = DatagramBuf.createRequestVector(builder, downloads)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, requestVector)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  const buf = builder.asUint8Array()
  return buf
}

export function sendFileDownloadRequests(requests: FileDownloadRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()
  sendWorkerDatagramBuf(createFileDownloadRequestDatagram(requests))
}

export function parseFileDownloadResponseDatagramBuf(
  datagram: FileDownloadResponseDatagramBuf,
): FileDownloadResponseDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    file: maybe(parseFile)(datagram.file()),
    offline: datagram.offline(),
    fileId: parseFileId(datagram.fileId()!),
    presignedUri: datagram.presignedUri() || undefined,
  }
}

/** Parse a Flatbuffers representation of a response */
export function handleFileDownloadResponseDatagramBuf(datagram: FileDownloadResponseDatagramBuf) {
  onFileDownloadResponse(parseFileDownloadResponseDatagramBuf(datagram))
}

export function onFileDownloadResponse(response: FileDownloadResponseDatagram) {
  const { emitInterfaceEvent } = useEvents()

  emitInterfaceEvent("fileDownloadResponse", response, response.requestId)

  if (response.error)
    return onFileDownloadErrored(response.error)
  if (!response.file)
    throw new Error("No file in download response")

  // TODO: Do we need to have properties from a download response?
  // const iFile = new StaticFile(response.file)
  // emitEntityEvent("fileProperties", iFile.fileId, iFile.inner(), response.offline)
}

export function onFileDownloadErrored(error: DatagramError,
) {
  const { notifyDatagramError } = useAlerts()
  return notifyDatagramError(error, "File download was unsuccessful")
}
